import React, { useState } from "react";
import * as Sentry from "@sentry/react";

import {
  Box,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";

import { grayLight, moovsBlue } from "design-system/colors";
import { CopyIcon, InfoIcon } from "design-system/icons";
import { useSnackbar } from "globals/hooks";
import useNotificationVariables from "../../../hooks/useNotificationVariables";

const SMS_MAX_LENGTH = 320;

type SmsInputWithSmartKeysProps = {
  smsContent: string;
  handleSmsContentChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  availablesVariables: string[];
};

function SmsInputWithSmartKeys(props: SmsInputWithSmartKeysProps) {
  const { smsContent, handleSmsContentChange, availablesVariables } = props;

  // hooks
  const snackbar = useSnackbar();
  const { notificationVariables, loading: loadingNotificationVariables } =
    useNotificationVariables(availablesVariables);

  // state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // event handlers
  const handleOpenSmartKeysMenu = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseSmartKeysMenu = () => {
    setAnchorEl(null);
  };

  const handleCopyToClipboard = async (value: string) => {
    try {
      await navigator.clipboard.writeText(`{${value}}`);
      snackbar.success(`Copied {${value}} to clipboard`);
      handleCloseSmartKeysMenu();
    } catch (err) {
      Sentry.captureException(err);
      snackbar.error(
        "Failed to copy smart key. Please type out smart key directly."
      );
    }
  };

  return (
    <>
      <Box display="flex" justifyContent="flex-end">
        <Button
          id="basic-button"
          onClick={handleOpenSmartKeysMenu}
          disabled={loadingNotificationVariables}
        >
          <Box minHeight="28px" display="flex" alignItems="center">
            {loadingNotificationVariables ? (
              <CircularProgress size={16} />
            ) : (
              "+ Smart Keys"
            )}
          </Box>
        </Button>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseSmartKeysMenu}
        PaperProps={{
          style: {
            minWidth: "250px",
            maxWidth: "3000px",
            boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.15)",
          },
          elevation: 0,
        }}
      >
        <Box px={2} mt={1}>
          <Box
            display="flex"
            bgcolor={grayLight}
            maxWidth="300px"
            p={1}
            borderRadius={1}
          >
            <Box ml={0.5} mr={1} mt={0.2}>
              <InfoIcon />
            </Box>
            <Box>
              <Typography variant="subtitle2">Add smart keys</Typography>
              <Typography variant="caption">
                Add smart keys to your SMS templates to add personalized details
                to the notifications you send out. To use them, just copy and
                paste them into your template.
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box px={2} pt={1.5} pb={0.5}>
          <Typography variant="overline">AVAILABLE KEYS</Typography>
        </Box>
        {notificationVariables.map((smartKey) => (
          <MenuItem
            onClick={() => handleCopyToClipboard(smartKey.key)}
            key={smartKey.key}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography variant="caption">
                  {smartKey.description}
                </Typography>
                <Typography sx={{ fontWeight: 500, color: moovsBlue }}>
                  {`{${smartKey.key}}`}
                </Typography>
              </Box>
              <Box ml={3}>
                <CopyIcon color={moovsBlue} />
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Menu>

      {/* SMS Customized Content */}
      <Box mb={2}>
        <Typography variant="h4">SMS Template</Typography>
      </Box>
      <TextField
        multiline
        rows={6}
        fullWidth
        variant="outlined"
        value={smsContent}
        onChange={handleSmsContentChange}
        inputProps={{
          maxLength: SMS_MAX_LENGTH,
        }}
        // Display character count as helper text
        helperText={`${smsContent.length}/${SMS_MAX_LENGTH} characters`}
      />
    </>
  );
}

export default SmsInputWithSmartKeys;
